/**
 * GSAP Animations
 * Performance-optimized animations synchronized with Lenis Smooth Scroll
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register plugins
gsap.registerPlugin(ScrollTrigger);

class Animations {
  constructor(lenis) {
    this.lenis = lenis;
    this.init();
  }
  
  init() {
    // Snappy defaults for professional feel
    gsap.defaults({
      ease: 'expo.out',
      duration: 0.8,
    });
    
    // Performance configs
    ScrollTrigger.config({ limitCallbacks: true });

    // Force Scroll to Top on Load (Aggressive)
    if (history.scrollRestoration) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    document.body.scrollTop = 0; // Safari
    document.documentElement.scrollTop = 0; // Chrome/Firefox

    // Additional safeguard for refresh
    window.onbeforeunload = function () {
      window.scrollTo(0, 0);
    };
    
    // Safe initialization with timeout fallback
    const startApp = () => {
      this.setInitialStates();
      this.setupHeroInteractions(); // Text splitting must happen BEFORE animation
      this.setupPageLoad();
      this.setupScrollAnimations();
      this.setupTimelineProgress();
      this.setupCounterAnimations();
      this.setupHoverEffects();
      this.setupScrollProgress(); // New Scroll Progress Bar
      this.setupLiquidWobble();
    };

    // Race between fonts ready and a 1.5s timeout to ensure site loads even if fonts fail
    Promise.race([
      document.fonts.ready,
      new Promise(resolve => setTimeout(resolve, 1500))
    ]).then(() => {
      startApp();
    });
  }
  
  // Advanced Organic Morphing - Mimics the "Liquid O" reference images
  setupLiquidWobble() {
    const targets = document.querySelectorAll('.portrait-frame, .pulsating-ring');
    const filter = document.querySelector('#liquid-wobble feTurbulence');
    if (!targets.length && !filter) return;

    let time = 0;
    
    const animate = () => {
      time += 0.005; // Smoothing factor
      
      // 1. Animate SVG Filter Turbulence (Organic Warping)
      if (filter) {
        const baseFreqX = 0.007 + Math.sin(time * 0.8) * 0.003;
        const baseFreqY = 0.009 + Math.cos(time * 0.6) * 0.003;
        filter.setAttribute('baseFrequency', `${baseFreqX} ${baseFreqY}`);
      }
      
      // 2. Animate Border Radius (Organic Shape Shifting)
      targets.forEach((el, i) => {
        const offset = i * 0.5; // Stagger the rings
        
        // Target values oscillating between 35% and 65% for a smooth, bubbled look
        const r1 = 50 + Math.sin(time + offset) * 15;
        const r2 = 50 + Math.cos(time * 0.8 + offset) * 15;
        const r3 = 50 + Math.sin(time * 1.2 + offset) * 15;
        const r4 = 50 + Math.cos(time * 0.5 + offset) * 15;
        
        const borderRadius = `${r1}% ${100-r1}% ${r2}% ${100-r2}% / ${r3}% ${r4}% ${100-r4}% ${100-r3}%`;
        
        // Directly set style to avoid GSAP string parsing issues with complex border-radius (the "/" syntax)
        el.style.borderRadius = borderRadius;
      });

      requestAnimationFrame(animate);
    };

    animate();
  }

  setInitialStates() {
    // Hero elements initial states - Hardware accelerated
    gsap.set('.hero-label', { opacity: 0, y: 30, willChange: 'transform, opacity' });
    gsap.set('.title-text', { opacity: 0, y: 60, willChange: 'transform, opacity' });
    gsap.set('.hero-tagline', { opacity: 0, y: 15, willChange: 'transform, opacity' });
    gsap.set('.hero-description', { opacity: 0, willChange: 'opacity' });
    gsap.set('.hero-description-card', { opacity: 0, y: 20, willChange: 'transform, opacity' });
    gsap.set('.hero-cta .btn', { opacity: 0, y: 15, willChange: 'transform, opacity' });
    gsap.set('.float-icon', { opacity: 0, scale: 0.8, willChange: 'transform, opacity' });
    gsap.set('.scroll-indicator', { opacity: 0, y: 10, willChange: 'transform, opacity' });
    gsap.set('.navbar', { opacity: 0, y: -10, willChange: 'transform, opacity' });
    
    // Section headers & general reveal elements
    gsap.set('.section-header .section-label, .section-header .section-title, .reveal-up', { 
      opacity: 0, 
      y: 20, 
      willChange: 'transform, opacity' 
    });

    // Specific items
    const items = document.querySelectorAll('.skill-card, .award-card, .timeline-item, .project-card, .contact-link, .stat, .form-group');
    items.forEach(el => gsap.set(el, { opacity: 0, y: 30, willChange: 'transform, opacity' }));
  }
  
  setupPageLoad() {
    const tl = gsap.timeline({ delay: 0.3 });
    
    tl.to('.hero-label', { opacity: 1, y: 0, duration: 0.5 })
      .to('.title-text', { opacity: 1, y: 0, duration: 0.8, stagger: 0.1 }, '-=0.2')
      .to('.hero-tagline', { opacity: 1, y: 0, duration: 0.5 }, '-=0.5')
      .to('.hero-description-card', { opacity: 1, y: 0, duration: 0.8 }, '-=0.4')
      .to('.hero-description', { opacity: 1, duration: 0.6 }, '-=0.6') // Reveal text slightly after card starts moving
      .to('.hero-cta .btn', { opacity: 1, y: 0, duration: 0.4, stagger: 0.05 }, '-=0.3')
      .to('.float-icon', { opacity: 0.2, scale: 1, duration: 0.6, stagger: 0.05 }, '-=0.2')
      .to('.scroll-indicator', { opacity: 1, y: 0, duration: 0.4 }, '-=0.1')
      .to('.navbar', { opacity: 1, y: 0, duration: 0.4 }, 0.4);
  }
  
  setupHeroInteractions() {
    const titleTexts = document.querySelectorAll('.title-text');
    const glassCards = document.querySelectorAll('.glass-card, .about-content'); // Include about-content explicitly if class not added yet
    
    // 1. Title Text Interactivity (Rubber Band)
    if (titleTexts.length > 0) {
        titleTexts.forEach(text => {
            // Title Text Logic
            
            const chars = text.textContent.split('');
            text.innerHTML = ''; 
            chars.forEach(char => {
                const span = document.createElement('span');
                span.textContent = char;
                span.className = 'char';
                if (char === ' ') span.style.width = '0.3em';
                text.appendChild(span);
                
                // Note: We deliberately DO NOT force initial state here via GSAP.
                // We let CSS handle the Idle State (Gradient for Light, White for Dark)
                // to avoid race conditions or overwriting correct CSS styles.
                
                // Add Theme Observer to reset inline styles on theme switch
                // This fixes the bug where text becomes invisible when switching themes
                const themeObserver = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                            const isLight = document.documentElement.getAttribute('data-theme') === 'light';
                            if (isLight) {
                                gsap.set(span, { 
                                    backgroundImage: 'linear-gradient(180deg, #ff3333 0%, #cc0000 100%)', // RESTORE Gradient
                                    webkitTextFillColor: 'transparent', 
                                    webkitTextStrokeColor: 'transparent',
                                    overwrite: 'auto'
                                });
                            } else {
                                gsap.set(span, { 
                                    backgroundImage: 'none', // FORCE remove any gradient from light mode
                                    webkitTextFillColor: '#ffffff', 
                                    webkitTextStrokeColor: '#ffffff',
                                    overwrite: 'auto'
                                });
                            }
                        }
                    });
                });
                
                themeObserver.observe(document.documentElement, { attributes: true });
                
                span.addEventListener('mouseenter', () => {
                   const isLight = document.documentElement.getAttribute('data-theme') === 'light';
                   
                   if (isLight) {
                       // Light Mode Hover: pure hollow red border
                       // We MUST remove the background gradient so 'transparent' fill shows the page background (white)
                       gsap.to(span, { 
                           backgroundImage: 'none', 
                           webkitTextFillColor: 'transparent', 
                           webkitTextStrokeColor: '#ff3333',
                           duration: 0.1, 
                           overwrite: 'auto'
                       });
                   } else {
                       // Dark Mode Hover: transparent fill (black bg) + red border
                       gsap.to(span, { 
                           backgroundImage: 'none', // Ensure no gradient artifact
                           webkitTextFillColor: 'transparent', 
                           webkitTextStrokeColor: '#ff3333',
                           duration: 0.1, 
                           overwrite: 'auto' 
                       });
                   }
                   
                   // Bounce Animation
                   gsap.timeline()
                    .to(span, { scaleY: 0.7, scaleX: 1.25, duration: 0.1, ease: 'power2.out' })
                    .to(span, { scaleY: 1, scaleX: 1, duration: 0.9, ease: 'elastic.out(1, 0.3)' });
                });
                
                span.addEventListener('mouseleave', () => {
                   // Check Theme for correct restore state
                   const isLight = document.documentElement.getAttribute('data-theme') === 'light';
                   
                   if (isLight) {
                       // Light Theme Restore: Restore Gradient + Transparent Fill
                       gsap.to(span, { 
                         backgroundImage: 'linear-gradient(180deg, #ff3333 0%, #cc0000 100%)',
                         webkitTextFillColor: 'transparent', 
                         webkitTextStrokeColor: 'transparent', 
                         duration: 0.3, 
                         overwrite: 'auto' 
                       });
                   } else {
                       // Dark Theme Restore: White Fill, White Border
                       gsap.to(span, { 
                         backgroundImage: 'none', // FORCE cleanup
                         webkitTextFillColor: '#ffffff', 
                         webkitTextStrokeColor: '#ffffff',
                         duration: 0.3, 
                         overwrite: 'auto' 
                       });
                   }
                });
            });
        });
    }

    // 2. Glass Card Shine Effect is now handled centrally in setupHoverEffects() 
    // to avoid duplicate event listeners and improve performance.
  }
  
  setupScrollAnimations() {
    // Reveal Section Headers and generic reveal-up elements
    gsap.utils.toArray('.section-header, .reveal-up').forEach((el) => {
      const targets = el.classList.contains('reveal-up') ? [el] : el.querySelectorAll('.section-label, .section-title');
      
      gsap.to(targets, {
        scrollTrigger: {
          trigger: el,
          start: 'top 92%',
          toggleActions: 'play none none none',
        },
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        overwrite: true
      });
    });
    
    // Efficient Batching for cards and stats
    ScrollTrigger.batch('.skill-card, .award-card, .timeline-item, .project-card, .stat, .contact-link, .form-group', {
      start: 'top 90%',
      onEnter: (elements) => {
        gsap.to(elements, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.08,
          overwrite: true
        });
        
        // Add active class to timeline items for scroll animation
        elements.forEach(el => {
            if (el.classList.contains('timeline-item')) {
                el.classList.add('active');
            }
        });
      },
      onLeaveBack: (elements) => {
         // Optional: Remove class when scrolling back up if desired, 
         // but usually "onEnter" is enough for a permanent "scrolled past" effect.
         // If "when scoll animation" means "while in view focus", we might need toggleActions.
         // But "occur when it being scrolled as well" implies triggering the effect.
         // Let's keep it simple: Add active when revealed.
         elements.forEach(el => {
            if (el.classList.contains('timeline-item')) {
                el.classList.remove('active'); // Remove to allow re-trigger or if we want it only when "focused".
                // Actually, let's make it smarter: Toggle based on center view
            }
         });
      },
      once: false // Enable re-triggering for the active state
    });

    // About Me Active State on Scroll
    const aboutContent = document.querySelector('.about-content');
    if (aboutContent) {
      ScrollTrigger.create({
        trigger: aboutContent,
        start: 'top 60%',
        end: 'bottom 40%',
        onEnter: () => aboutContent.classList.add('active'),
        onLeave: () => aboutContent.classList.remove('active'),
        onEnterBack: () => aboutContent.classList.add('active'),
        onLeaveBack: () => aboutContent.classList.remove('active')
      });
    }

    // Dedicated Trigger for Timeline Active State (Focus Effect)
    // Dedicated Trigger for Timeline Active State (Focus Effect)
    const timelineItems = gsap.utils.toArray('.timeline-item');
    timelineItems.forEach((item, index) => {
      // Use the item (container) to include padding and bridge gaps
      const isLast = index === timelineItems.length - 1;
      
      const st = ScrollTrigger.create({
        trigger: item, 
        start: 'top 50%', // Center Lock: Matches timeline line exactly
        end: isLast ? 'bottom 20%' : 'bottom 50%', // Handoff at center
        onEnter: () => item.classList.add('active'),
        onLeave: () => item.classList.remove('active'),
        onEnterBack: () => item.classList.add('active'),
        onLeaveBack: () => item.classList.remove('active')
      });
      
      // Mouse Focus (Exclusive)
      item.addEventListener('mouseenter', () => {
          gsap.utils.toArray('.timeline-item').forEach(el => el.classList.remove('active')); // Clear others
          item.classList.add('active'); // Set current
      });
      
      item.addEventListener('mouseleave', () => {
         // Only remove active if we are NOT in the active scroll zone
         if (!st.isActive) {
            item.classList.remove('active');
         } else {
             // Keep active if scrolled to
             item.classList.add('active');
         }
      });
    });

    // Subtle Parallax
    gsap.utils.toArray('.float-icon').forEach((icon) => {
      const speed = parseFloat(icon.dataset.speed) || 0.5;
      gsap.to(icon, {
        scrollTrigger: {
          trigger: '.hero',
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
        y: window.innerHeight * speed * 0.2,
        ease: 'none',
      });
    });
  }
  
  setupTimelineProgress() {
    const timelineLine = document.querySelector('.timeline-progress');
    if (timelineLine) {
      gsap.to(timelineLine, {
        scrollTrigger: {
          trigger: '.timeline',
          start: 'top 50%', // Sync with cards
          end: 'bottom 50%',
          scrub: true,
        },
        height: '100%',
        ease: 'none',
      });
    }
  }
  
  setupCounterAnimations() {
    const counters = document.querySelectorAll('.stat-value');
    
    counters.forEach((counter) => {
      const target = parseInt(counter.dataset.count, 10);
      const suffix = counter.dataset.suffix || '';
      
      // Dynamic duration: Fast for small numbers, slow for big ones
      const animDuration = target > 100 ? 2.5 : 1.0;
      
      // Use a proxy object for reliable numeric tweening
      const proxy = { val: 0 };
      
      gsap.to(proxy, {
        scrollTrigger: { 
          trigger: counter, 
          start: 'top 92%',
          toggleActions: 'play none none none'
        },
        val: target,
        duration: animDuration,
        ease: 'power2.out',
        onUpdate: function() {
          counter.innerText = Math.floor(proxy.val) + suffix;
        }
      });
    });
  }
  
  setupHoverEffects() {
    if (window.matchMedia('(hover: hover)').matches) {
      const cards = document.querySelectorAll('.skill-card, .award-card, .project-card, .glass-card, .portrait-frame');
      
      cards.forEach((card) => {
        const isPortrait = card.classList.contains('portrait-frame');
        const isHeroCard = card.classList.contains('hero-description-card');
        
        // Skip hover interactions for the hero description card (keep it fixed)
        if (isHeroCard) return;

        card.addEventListener('mousemove', (e) => {
          const rect = card.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          
          // Update glow position (CSS variables)
          const xPercent = (x / rect.width) * 100;
          const yPercent = (y / rect.height) * 100;
          card.style.setProperty('--mouse-x', `${xPercent}%`);
          card.style.setProperty('--mouse-y', `${yPercent}%`);
          
          // 3D Tilt Effect - Weakened intensity for usability
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          // Increased divisor from 10/15 to 30/40 for much subtler movement
          const rotateX = (centerY - y) / (isPortrait ? 40 : 30);
          const rotateY = (x - centerX) / (isPortrait ? 40 : 30);
          
          gsap.to(card, {
            rotateX: rotateX,
            rotateY: rotateY,
            scale: isPortrait ? 1.03 : 1.01, // Very subtle scale
            duration: 0.6,
            ease: 'power2.out',
            overwrite: 'auto'
          });

          // Portrait Simple Parallax (No Morph)
          if (isPortrait) {
            const img = card.querySelector('.portrait-img');
            if (img) {
               gsap.to(img, {
                 scale: 1.1,
                 filter: 'grayscale(0%) contrast(1.1) brightness(1.05)',
                 x: (x - centerX) * 0.03, // Slight opposite move
                 y: (y - centerY) * 0.03,
                 duration: 0.6,
                 ease: 'power2.out',
                 overwrite: 'auto'
               });
            }
          }
        });
        
        card.addEventListener('mouseleave', () => {
          gsap.to(card, {
            rotateX: 0,
            rotateY: 0,
            scale: 1,
            x: 0,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            overwrite: 'auto'
          });

          if (isPortrait) {
            const img = card.querySelector('.portrait-img');
            if (img) {
              gsap.to(img, {
                scale: 1,
                filter: 'grayscale(100%) contrast(1.1) brightness(1.05)',
                x: 0,
                y: 0,
                duration: 0.8,
                ease: 'power2.out',
                overwrite: 'auto'
              });
            }
          }
        });
      });
    }
  }
  setupScrollProgress() {
    const progressBar = document.querySelector('.scroll-progress-bar');
    if (progressBar) {
      if (this.lenis) {
         // robust lenis progress
         this.lenis.on('scroll', ({ progress }) => {
            gsap.set(progressBar, { width: `${progress * 100}%` });
         });
      } else {
         // fallback
         gsap.to(progressBar, {
          width: '100%',
          ease: 'none',
          scrollTrigger: {
            start: 0,
            end: 'max',
            scrub: 0,
            invalidateOnRefresh: true
          }
         });
      }
    }
  }
}

export default Animations;
